import { Destructor, VoidCallback } from '@deqo/foundation';
import { fromBase64, toBase64 } from 'js-base64';
import { ServiceSymbol } from '../app/Container';
import { hash } from '../CodesThatsGottaGoSomewhereElse';
import { StorageService } from './StorageService';

const clipboard = navigator.clipboard;

export interface ClipData {

    types(): string[];

    get(type: string): Blob | File;

    has(type: string): boolean;

    prepareWrite(): Record<string, Blob | File>;
}

export class ClipDataBuilder {

    private formats = {} as { [type: string]: Blob | File };

    public setDeqoData(value: string) {
        this.setAsBlob('x/deqo-data', JSON.stringify(value));
    }

    public setText(value: string) {
        this.setAsBlob('text/plain', value);
    }

    public setHtml(value: string) {
        this.setAsBlob('text/html', value);
    }

    public setHtmlWithEmbeddedData(text: string, data: unknown) {
        this.setAsBlob('text/html', embedDataInHtml(text, data));
    }

    public setBlob(blob: Blob) {
        this.formats[blob.type] = blob;
    }

    public setAsBlob(type: string, value: any) {
        this.formats[type] = new Blob([value], { type });
    }

    public build() {
        return new ClipDataImpl(this.formats);
    }
}

class ClipDataImpl implements ClipData {

    private formats = {} as { [type: string]: any };

    constructor(formats?: { [type: string]: any }) {
        Object.assign(this.formats, formats);
    }

    public types(): string[] {
        return Object.keys(this.formats);
    }

    public get(type: string): any {
        if (!this.has(type)) throw new Error(`${type} data not present in ClipData.`);
        return this.formats[type];
    }

    public set(type: string, value: any): void {
        this.formats[type] = value;
    }

    public has(type: string): boolean {
        return this.formats[type] !== undefined;
    }

    public prepareWrite(): any {
        const supported = Object.entries(this.formats)
            .filter(([key]) => key === 'text/plain' || key === 'text/html' || key === 'image/png');
        return Object.fromEntries(supported);
    }
}

export type ClipboardCallback = (data: ClipData) => void;

export interface ClipboardService {

    on(event: 'paste', callback: ClipboardCallback): Destructor;

    read(): Promise<ClipData>;

    write(data: ClipData): Promise<void>;
}

export const ClipboardService = ServiceSymbol<ClipboardService>('ClipboardService');

export const ClipboardFactory = {
    createForNavigator() {
        // If navigator does not support read/write, use fallback service with crappy local storage hack.
        return !!clipboard && !!clipboard.read && !!clipboard.write ? new DefaultClipboardService() : new FallbackClipboardService();
    }
};

export class DefaultClipboardService implements ClipboardService {

    public on(event: 'paste', callback: ClipboardCallback): VoidCallback {
        const handler = (e: ClipboardEvent) => callback(readFromClipboardEvent(e, false));
        window.addEventListener('paste', handler);
        return () => window.removeEventListener('paste', handler);
    }

    public async read(): Promise<ClipData> {
        return readFromClipboardItems((await clipboard.read()) ?? []);
    }

    public async write(data: ClipData): Promise<void> {
        const output = data.prepareWrite();
        await clipboard.write([new ClipboardItem(output as any)]);
    }
}

export class FallbackClipboardService implements ClipboardService {

    // Really this should be injected but it's kinda hard
    private localData = StorageService.createLocal();

    public on(event: 'paste', callback: ClipboardCallback): VoidCallback {
        const handler = (e: ClipboardEvent) => callback(readFromClipboardEvent(e, true));
        window.addEventListener('paste', handler);
        return () => window.removeEventListener('paste', handler);
    }

    public async read(): Promise<ClipData> {

        const text = await clipboard.readText();
        const clip = new ClipDataImpl();
        clip.set('text/plain', text);

        if (text !== null && text !== undefined) {
            const key = hash('sha256', text);
            const entry = this.localData.get('deqo/clipboard');
            if (!!entry && entry.key === key) {
                clip.set('x/deqo-data', entry.data);
            }
        }

        return clip;
    }

    public async write(clip: ClipData): Promise<void> {

        if (!clip.has('text/plain')) {
            throw new Error('ClipData must include text/plain type.');
        }

        const text = await clip.get('text/plain').text();

        // Fallback for lack of html embed is to store copied data in local storage keyed on the text version.
        if (clip.has('x/deqo-data')) {
            const key = hash('sha256', normalize(text));
            const data = await clip.get('x/deqo-data').text();
            this.localData.put('deqo/clipboard', { key, data });
        }

        await clipboard.writeText(text);
    }
}

function normalize(str: string): string {
    if (str) {
        str = str.replace(/\r/g, '');
    }
    return str;
}

const htmlFormat = `
    <html>
        <body>
            <!--<deqo>$EMBED$</deqo>-->
            $TEXT$
        </body>
    </html>
`;

const embedDataInHtml = (text: string, data: unknown) => {
    const embed = toBase64(JSON.stringify(data));
    const html = htmlFormat.replace('$EMBED$', embed).replace('$TEXT$', text);
    return html;
};

const extractDataFromHtml = (html: string) => {
    const data = html.match(/<!--<deqo>(.*?)<\/deqo>-->/) ?? [];
    if (!data.length) return null;
    try {
        return fromBase64(data[1]);
    }
    catch {
        return null;
    }
};

const readFromClipboardEvent = (e: ClipboardEvent, fallbackMode: boolean) => {

    const dt = e.clipboardData;
    const data = new ClipDataImpl();

    for (const file of dt.files) {
        data.set(file.type, file);
    }

    for (const type of dt.types) {
        const text = dt.getData(type);
        data.set(type, new Blob([text], { type }));
        // If HTML look for embedded data
        if (type === 'text/html') {
            const embedded = extractDataFromHtml(text);
            if (embedded) {
                data.set('x/deqo-data', new Blob([embedded], { type: 'x/deqo-data' }));
            }
        }
        // If text and on fallback mode, look for local fallback data
        if (type === 'text/plain' && fallbackMode) {
            const localData = StorageService.createLocal();

            if (text !== null && text !== undefined) {
                const key = hash('sha256', text);
                const entry = localData.get('deqo/clipboard');
                if (!!entry && entry.key === key) {
                    data.set('x/deqo-data', new Blob([entry.data], { type: 'x/deqo-data' }));
                }
            }
        }
    }

    return data;
};

const readFromClipboardItems = async (clipboardItems: ClipboardItem[]) => {

    const data = new ClipDataImpl();

    for (const item of clipboardItems) {
        for (const type of item.types) {
            const blob = await item.getType(type);
            data.set(type, blob);
            // If HTML look for embedded data
            if (type === 'text/html') {
                const text = await blob.text();
                const embedded = extractDataFromHtml(text);
                if (embedded) {
                    data.set('x/deqo-data', new Blob([embedded], { type: 'x/deqo-data' }));
                }
            }
        }
    }

    return data;
};
